import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Wrapper from "../../helpers/Wrapper";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../helpers/Axios";
import LinkButton from "../../ui/LinkButton";
import { displayImage } from "../../helpers/config";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

function Banner() {
  const [banners, setBanners] = useState([]);

  const [currentSlide, setCurrentSlides] = useState(0);
  const [hover, sethover] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ["banner"],
    queryFn: async () => {
      const res = await apiClient.get("/shop/banner");
      return res.data;
    },
  });

  useEffect(() => {
    setBanners(data || []);
  }, [data]);

  const nextSlide = useCallback(() => {
    setCurrentSlides((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlides((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  }, [banners.length]);

  const nextSlideRef = useRef(nextSlide);

  useEffect(() => {
    nextSlideRef.current = nextSlide;
  }, [nextSlide]);

  // autoplay
  useEffect(() => {
    if (hover || banners.length <= 1) return;

    const timer = setInterval(() => nextSlideRef.current(), 5000);

    return () => clearInterval(timer);
  }, [hover, nextSlide, banners.length]);

  const textVariant = {
    initial: {
      y: -250,
      opacity: 0,
    },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.9,
        staggerChildren: 0.1,
      },
    },
  };

  if (isPending) {
    return (
      <div className="w-full relative xl:h-[calc(100vh-90px)] flex items-center overflow-hidden bg-gray-50 animate-pulse"></div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      onMouseEnter={() => sethover(true)}
      onMouseLeave={() => sethover(false)}
      className="w-full relative h-[calc(100vh-80px)] xl:h-[calc(100vh-130px)] overflow-hidden"
    >
      {/* Datas */}

      {data && data?.length > 0 && (
        <div className="w-full h-full">
          <AnimatePresence>
            {data?.map(
              (banner, i) =>
                currentSlide === i && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    key={i}
                    className="w-full h-full flex items-center"
                  >
                    {/* background image */}
                    <div className="absolute top-0 left-0 w-full h-full aspect-video">
                      <motion.img
                        crossOrigin="anonymous"
                        alt=""
                        loading="lazy"
                        src={displayImage(banner?.image)}
                        className="w-full object-cover h-full bg-white"
                      />
                    </div>

                    {/* Contents */}
                    <div className="absolute w-full 2xl:ml-24">
                      <Wrapper>
                        <motion.div
                          initial="initial"
                          animate="animate"
                          variants={textVariant}
                        >
                          <motion.p
                            variants={textVariant}
                            className="font-medium text-sm uppercase"
                          >
                            {banner?.sub_title}
                          </motion.p>

                          <motion.h1
                            variants={textVariant}
                            className=" text-4xl md:text-6xl mt-3 break-words"
                          >
                            {banner?.title}
                          </motion.h1>

                          <motion.p
                            variants={textVariant}
                            className="mt-4 text-gray-500 xl:text-lg mb-10 max-w-[600px]"
                          >
                            {banner?.description}
                          </motion.p>

                          <LinkButton black to={banner?.link_url}>
                            {banner?.link_name}
                          </LinkButton>
                        </motion.div>
                      </Wrapper>
                    </div>
                  </motion.div>
                ),
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Buttons */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ x: hover ? 0 : -100, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={prevSlide}
        className="absolute top-1/2 -translate-y-1/2 bg-white p-5 rounded-full hover:bg-black hover:text-white
         shadow-lg h-14 w-14  items-center justify-center left-8 font-medium  hidden xl:flex"
      >
        <FaChevronLeft />
      </motion.button>

      <motion.button
        onClick={nextSlide}
        initial={{ opacity: 0 }}
        animate={{ x: hover ? 0 : 100, opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="absolute top-1/2 -translate-y-1/2 bg-white p-5 rounded-full hover:bg-black hover:text-white
         shadow-lg h-14 w-14 items-center justify-center right-8  hidden xl:flex"
      >
        <FaChevronRight />
      </motion.button>

      {/* Dots */}

      <div className="absolute bottom-5 w-full flex justify-center items-center gap-3 z-[999]">
        {banners?.map((_, i) => (
          <button
            onClick={() => {
              setCurrentSlides(i);
            }}
            key={i}
            className={`${
              i === currentSlide ? "bg-black w-8" : "bg-gray-400 w-3"
            } h-3 transition rounded-full`}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default Banner;
